import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Activity, TrendingUp, Zap, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highestLeverageMove, setHighestLeverageMove] = useState(null);
  const [loadingLeverageMove, setLoadingLeverageMove] = useState(true);

  useEffect(() => {
    fetchMarketOverview();
    fetchHighestLeverageMove();
  }, []);

  const fetchMarketOverview = async () => {
    try {
      const response = await axios.get(`${API}/market/overview`, {
        headers: getAuthHeaders()
      });
      setMarketData(response.data);
    } catch (error) {
      console.error('Failed to fetch market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHighestLeverageMove = async () => {
    try {
      const response = await axios.get(`${API}/wealth/highest-leverage-move`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setHighestLeverageMove(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch highest leverage move:', error);
    } finally {
      setLoadingLeverageMove(false);
    }
  };

  const stats = [
    { label: 'System Status', value: 'OPERATIONAL', icon: Activity, color: 'text-green-500' },
    { label: 'Active Projects', value: '0', icon: Zap, color: 'text-primary' },
    { label: 'Portfolio Value', value: '$0', icon: TrendingUp, color: 'text-primary' },
    { label: 'Pending Actions', value: '0', icon: AlertCircle, color: 'text-muted-foreground' },
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <span className="text-sm font-bold font-mono text-amber-500">L</span>
            </div>
            <h1 className="text-3xl font-bold text-white">Command Center</h1>
          </div>
          <p className="text-zinc-600 text-sm ml-[52px]">Your strategic operations hub</p>
        </div>

        {/* 🔥 HIGHEST LEVERAGE MOVE - Core Wealth Intelligence */}
        {!loadingLeverageMove && highestLeverageMove && (
          <div className="glass-card p-8 border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/10 to-orange-500/5" data-testid="highest-leverage-move">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Highest Leverage Move Today</h2>
                  <p className="text-sm text-yellow-400/80">AI-calculated optimal wealth move for your current capital state</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-yellow-400/60 mb-1">Confidence Score</div>
                <div className="text-3xl font-bold font-mono text-yellow-400">{highestLeverageMove.confidence_score}%</div>
              </div>
            </div>

            <div className="bg-black/30 rounded-lg p-6 mb-6 border border-yellow-500/20">
              <h3 className="text-xl font-bold text-white mb-2">{highestLeverageMove.headline_move}</h3>
              <p className="text-muted-foreground mb-4">{highestLeverageMove.reasoning_summary}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Capital Required</div>
                  <div className="text-lg font-bold font-mono text-white">
                    ${highestLeverageMove.capital_required?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Expected Return</div>
                  <div className="text-lg font-bold font-mono text-green-400">
                    +${highestLeverageMove.expected_return?.toLocaleString() || '0'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Timeframe</div>
                  <div className="text-lg font-bold text-white">{highestLeverageMove.timeframe}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Risk Rating</div>
                  <div className="flex items-center gap-1">
                    {[...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-4 rounded-sm ${
                          i < (highestLeverageMove.risk_rating || 0)
                            ? 'bg-yellow-500'
                            : 'bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-black/40 rounded p-4 mb-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Why It Fits You</div>
                <p className="text-sm text-white/90">{highestLeverageMove.why_it_fits || highestLeverageMove.reasoning_summary}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(`/opportunities`)}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-6 rounded-lg transition-colors uppercase tracking-widest text-sm"
                  data-testid="view-opportunity-button"
                >
                  View Full Details
                </button>
                <button
                  onClick={() => {
                    // Simulate move
                    navigate(`/opportunities?simulate=${highestLeverageMove.opportunity_id}`);
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-6 rounded-lg transition-colors border border-white/20 uppercase tracking-widest text-sm"
                  data-testid="simulate-impact-button"
                >
                  Simulate Impact
                </button>
              </div>
            </div>

            {/* Alternative Moves */}
            {highestLeverageMove.alternative_moves && highestLeverageMove.alternative_moves.length > 0 && (
              <div>
                <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Alternative Moves</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {highestLeverageMove.alternative_moves.map((alt, index) => (
                    <div key={index} className="bg-black/20 rounded-lg p-4 border border-white/10 hover:border-yellow-500/30 transition-colors cursor-pointer">
                      <div className="text-sm font-semibold text-white mb-2">{alt.title}</div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>${alt.capital_required?.toLocaleString() || '0'}</span>
                        <span className="text-green-400">+${alt.expected_return?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loadingLeverageMove && !highestLeverageMove && (
          <div className="glass-card p-6 border border-yellow-500/20 bg-yellow-500/5">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
              <div>
                <h3 className="font-bold text-white">Wealth Intelligence Not Available</h3>
                <p className="text-sm text-muted-foreground">
                  Connect your bank accounts in Wealth Command Center to unlock highest-leverage move recommendations.
                </p>
              </div>
              <button
                onClick={() => navigate('/wealth')}
                className="ml-auto bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-2 px-6 rounded-lg uppercase tracking-widest text-xs"
              >
                Connect Banks
              </button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="glass-card p-8 border border-white/10 hover:border-primary/50 transition-colors" data-testid={`stat-card-${index}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">{stat.label}</span>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <p className="text-3xl font-bold font-mono text-white">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Market Overview */}
        <div className="glass-card p-8 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Market Overview</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              <p className="text-muted-foreground mt-4">Loading market data...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {marketData.map((quote, index) => (
                <div key={index} className="bg-secondary/30 p-6 border border-border" data-testid={`market-${quote.symbol}`}>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{quote.symbol}</p>
                  <p className="text-2xl font-bold font-mono text-white mb-1">
                    ${quote.price ? quote.price.toFixed(2) : 'N/A'}
                  </p>
                  {quote.change_percent && (
                    <p className={`text-sm font-mono ${
                      quote.change >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {quote.change_percent}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Voice & Chat CTA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button 
            onClick={() => navigate('/ai-command')}
            className="glass-card p-8 border border-amber-500/20 hover:border-amber-500/40 transition-all text-left group"
            data-testid="quick-action-chat"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/15 transition-colors">
                <span className="text-lg font-bold font-mono text-amber-500">L</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Talk to LEE</h3>
                <p className="text-sm text-zinc-500">Persistent intelligence threads</p>
              </div>
            </div>
            <p className="text-xs text-zinc-600">Give commands, discuss strategy, think through problems together</p>
          </button>
          
          <button 
            onClick={() => navigate('/voice')}
            className="glass-card p-8 border border-white/10 hover:border-amber-500/20 transition-all text-left group"
            data-testid="voice-conversation-button"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center group-hover:bg-zinc-700 transition-colors">
                <Zap className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Voice Mode</h3>
                <p className="text-sm text-zinc-500">Hands-free conversation</p>
              </div>
            </div>
            <p className="text-xs text-zinc-600">Speak naturally — LEE listens, thinks, and responds</p>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/inventions')}
            className="glass-card p-6 border border-white/10 hover:border-amber-500/20 transition-all text-left"
            data-testid="quick-action-create"
          >
            <p className="text-sm font-semibold text-white mb-1">Inventions</p>
            <p className="text-xs text-zinc-600">Idea to production pipeline</p>
          </button>
          
          <button 
            onClick={() => navigate('/wealth')}
            className="glass-card p-6 border border-white/10 hover:border-amber-500/20 transition-all text-left"
            data-testid="quick-action-wealth"
          >
            <p className="text-sm font-semibold text-white mb-1">Wealth Engine</p>
            <p className="text-xs text-zinc-600">Financial intelligence</p>
          </button>

          <button 
            onClick={() => navigate('/progress')}
            className="glass-card p-6 border border-white/10 hover:border-amber-500/20 transition-all text-left"
          >
            <p className="text-sm font-semibold text-white mb-1">Progress</p>
            <p className="text-xs text-zinc-600">Track all active projects</p>
          </button>
        </div>

        {/* Capabilities */}
        <div className="glass-card p-8 border border-white/10">
          <h2 className="text-lg font-bold text-white mb-6 uppercase tracking-wider">Capabilities</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Invention Pipeline', desc: 'Spec, BOM, sourcing, manufacturing — from idea to production' },
              { title: 'Wealth Intelligence', desc: 'Portfolio analysis, opportunity scanning, risk assessment' },
              { title: 'Strategic Thinking', desc: 'Decision frameworks, tradeoff analysis, execution roadmaps' },
              { title: 'Supply Chain', desc: 'Vendor sourcing, RFQ automation, cost optimization' },
            ].map((cap, i) => (
              <div key={i} className="space-y-2">
                <h3 className="text-sm font-semibold text-white">{cap.title}</h3>
                <p className="text-xs text-zinc-600 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
